import { Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { IMessageContentData } from '../../../../models/common/IMessageContentData';

@Component({
  selector: 'app-message-content-dialog',
  imports: [MatDialogModule,
            MatButtonModule
  ],
  templateUrl: './message-content-dialog.component.html',
  styleUrl: './message-content-dialog.component.scss'
})
export class MessageContentDialogComponent {
  renderedBodyHtml = '';

  constructor(
    private readonly dialogRef: MatDialogRef<MessageContentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IMessageContentData) {}

  close(): void {
    this.dialogRef.close();
  }

  onBodyClick(event: MouseEvent): void {
    const anchor = (event.target as HTMLElement | null)?.closest('a');
    const href = anchor?.getAttribute('href')?.trim();

    if (!anchor || !href || !/^(https?:|mailto:|tel:)/i.test(href)) {
      return;
    }

    event.preventDefault();
    window.open(href, '_blank', 'noopener,noreferrer');
  }

  async ngOnInit(): Promise<void> {
    if (this.data && this.data.title && this.data.body) {
      await this.convertMsgBodyToHtml(this.data.body);
    }
  }

  async convertMsgBodyToHtml(body: string): Promise<void> {
    const decodedBody = await this.decompress(body);
    const renderedContent = this.renderDecodedContent(decodedBody);

    this.renderedBodyHtml = renderedContent;
    this.data.body = renderedContent;
  }

  private async decompress(base64String: string): Promise<string> {
    try {
      if (!base64String) {
        return '';
      }

      const compressedBytes = this.base64StringToBytes(base64String);
      const decompressedBytes = await this.decompressBytes(compressedBytes);
      return this.unicodeBytesToString(decompressedBytes);
    } catch (ex) {
      console.error('Utility->Decompress', ex);
      return '';
    }
  }

  private base64StringToBytes(base64String: string): Uint8Array {
    const binaryString = atob(base64String);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes;
  }

  private async decompressBytes(compressedBytes: Uint8Array): Promise<Uint8Array> {
    if (typeof DecompressionStream === 'undefined') {
      throw new Error('DecompressionStream not supported in this browser');
    }

    const normalizedBuffer = new ArrayBuffer(compressedBytes.byteLength);
    const normalizedBytes = new Uint8Array(normalizedBuffer);
    normalizedBytes.set(compressedBytes);

    const compressedBlob = new Blob([normalizedBuffer]);
    const decompressedStream = compressedBlob.stream().pipeThrough(new DecompressionStream('gzip'));
    const decompressedBuffer = await new Response(decompressedStream).arrayBuffer();

    return new Uint8Array(decompressedBuffer);
  }

  private unicodeBytesToString(bytes: Uint8Array): string {
    // C# Encoding.Unicode maps to UTF-16 little-endian.
    return new TextDecoder('utf-16le').decode(bytes);
  }

  private renderDecodedContent(decodedBody: string): string {
    const normalizedBody = decodedBody.replace(/\0/g, '').trim();

    if (!this.isMimeMultipart(normalizedBody)) {
      return normalizedBody;
    }

    const parsedDocument = this.parseMimeDocument(normalizedBody);
    if (parsedDocument) {
      return this.inlineMimeResources(parsedDocument.html, parsedDocument.resources);
    }

    const fallbackHtml = this.extractHtmlFromMimeFallback(normalizedBody);
    return fallbackHtml || normalizedBody;
  }

  private isMimeMultipart(value: string): boolean {
    return /mime-version:/i.test(value) && /content-type:\s*multipart\//i.test(value);
  }

  private parseMimeDocument(mimeContent: string): { html: string; resources: Record<string, string> } | null {
    const boundaryMatch = /boundary="([^"]+)"/i.exec(mimeContent)
      ?? /boundary=([^;\r\n]+)/i.exec(mimeContent);

    if (!boundaryMatch) {
      return null;
    }

    const parts = this.splitMimeParts(mimeContent, boundaryMatch[1].trim());
    let htmlPart = '';
    const resources: Record<string, string> = {};

    for (const part of parts) {
      const parsedPart = this.parseMimePart(part);
      if (!parsedPart) {
        continue;
      }

      if (!htmlPart && /text\/html/i.test(parsedPart.contentType)) {
        htmlPart = parsedPart.content;
        continue;
      }

      const dataUrl = this.createResourceDataUrl(parsedPart);
      if (!dataUrl) {
        continue;
      }

      for (const key of this.getResourceKeys(parsedPart.headers)) {
        resources[key] = dataUrl;
      }
    }

    if (!htmlPart) {
      return null;
    }

    return {
      html: this.extractHtmlBody(htmlPart),
      resources
    };
  }

  private splitMimeParts(mimeContent: string, boundary: string): string[] {
    const normalizedContent = mimeContent.replace(/\r\n/g, '\n');
    const boundaryMarker = `--${boundary}`;

    return normalizedContent
      .split(boundaryMarker)
      .map((part) => part.trim())
      .filter((part) => part.length > 0 && part !== '--');
  }

  private parseMimePart(part: string): { contentType: string; content: string; headers: string } | null {
    const normalizedPart = part.replace(/^\n+|\n+$/g, '').replace(/\n--$/, '');
    const separatorMatch = /\n\n/.exec(normalizedPart);

    if (!separatorMatch) {
      return null;
    }

    const headers = normalizedPart.slice(0, separatorMatch.index);
    const body = normalizedPart.slice(separatorMatch.index + separatorMatch[0].length);
    const contentTypeMatch = /content-type:\s*([^;\n]+)/i.exec(headers);

    if (!contentTypeMatch) {
      return null;
    }

    const contentType = contentTypeMatch[1].trim();
    const encodingMatch = /content-transfer-encoding:\s*([^;\n]+)/i.exec(headers);
    const charsetMatch = /charset\s*=\s*"?([^";\n]+)"?/i.exec(headers);
    const encoding = encodingMatch ? encodingMatch[1].trim().toLowerCase() : '7bit';
    const charset = charsetMatch ? charsetMatch[1].trim().toLowerCase() : 'utf-8';

    return {
      contentType,
      headers,
      content: this.decodeMimeContent(body, encoding, contentType, charset)
    };
  }

  private decodeMimeContent(content: string, encoding: string, contentType: string, charset: string): string {
    if (encoding === 'quoted-printable') {
      return this.decodeQuotedPrintable(content);
    }

    if (encoding === 'base64') {
      const normalizedBase64 = content.replace(/\s/g, '');

      if (/^text\//i.test(contentType)) {
        return this.base64ToText(normalizedBase64, charset);
      }

      return normalizedBase64;
    }

    return content.trim();
  }

  private decodeQuotedPrintable(value: string): string {
    return value
      .replace(/=\r?\n/g, '')
      .replace(/=([0-9A-Fa-f]{2})/g, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)));
  }

  private base64ToText(base64: string, charset: string): string {
    try {
      const binaryString = atob(base64);
      const bytes = new Uint8Array(binaryString.length);

      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const normalizedCharset = charset === 'unicode' ? 'utf-16le' : charset;
      return new TextDecoder(normalizedCharset).decode(bytes);
    } catch {
      return '';
    }
  }

  private createResourceDataUrl(part: { contentType: string; content: string }): string | null {
    if (!/^image\//i.test(part.contentType)) {
      return null;
    }

    return `data:${part.contentType};base64,${part.content.replace(/\s/g, '')}`;
  }

  private getResourceKeys(headers: string): string[] {
    const keys: string[] = [];
    const contentIdMatch = /content-id:\s*<([^>]+)>/i.exec(headers);
    const contentLocationMatch = /content-location:\s*([^\n]+)/i.exec(headers);

    if (contentIdMatch) {
      const contentId = contentIdMatch[1].trim();
      keys.push(contentId, contentId.toLowerCase(), `cid:${contentId}`, `cid:${contentId.toLowerCase()}`);
    }

    if (contentLocationMatch) {
      const location = contentLocationMatch[1].trim();
      const fileName = location.split('/').pop()?.split('\\').pop();

      keys.push(location, location.toLowerCase());
      if (fileName) {
        keys.push(fileName, fileName.toLowerCase());
      }
    }

    return keys;
  }

  private extractHtmlBody(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const styles = Array.from(doc.head.querySelectorAll('style'))
      .map((style) => style.outerHTML)
      .join('');
    const bodyHtml = doc.body?.innerHTML?.trim();

    return `${styles}${bodyHtml || html}`;
  }

  private inlineMimeResources(html: string, resources: Record<string, string>): string {
    return html.replace(/(src|href)="([^"]+)"/gi, (match, attribute: string, rawValue: string) => {
      const value = rawValue.trim();
      const fileName = value.split('/').pop()?.split('\\').pop() ?? value;
      const replacement = resources[value]
        || resources[value.toLowerCase()]
        || resources[fileName]
        || resources[fileName.toLowerCase()];

      return replacement ? `${attribute}="${replacement}"` : match;
    });
  }

  private extractHtmlFromMimeFallback(mimeContent: string): string | null {
    const decodedContent = this.decodeQuotedPrintable(mimeContent);
    const htmlMatch = /<html[\s\S]*<\/html>/i.exec(decodedContent) ?? /<html[\s\S]*<\/html>/i.exec(mimeContent);

    if (htmlMatch) {
      return this.extractHtmlBody(htmlMatch[0]);
    }

    const bodyMatch = /<body[\s\S]*<\/body>/i.exec(decodedContent) ?? /<body[\s\S]*<\/body>/i.exec(mimeContent);
    return bodyMatch ? bodyMatch[0] : null;
  }
}
